import {Action} from "redux-actions";
import {BILL_OF_MATERIAL_REMOVED, BILL_OF_MATERIALS_FETCHED, BILLS_OF_MATERIALS_FETCHED} from "./actionType";
import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import {Dispatch} from "redux";
import {api} from "../../../api/api";
import {BillOfMaterialsPreviewDetails} from "../../../model/job/BillOfMaterialsPreviewDetails";
import {CreateUpdateBillOfMaterialsPreviewAssetApiDto, PriceApiDto} from "../../../api/model/rest";
import Price from "../../../model/common/Price";
import {BillOfMaterialsFinaliseOptions} from "../../../components/job/billOfMaterials/details/FinalisingBillOfMaterialsModal";
import SuggestedAssetPrice from "../../../model/job/SuggestedAssetPrice";
import {Option} from "ts-option";
import {Bom} from "../../../model/job/Bom";
import {BillOfMaterialsPreview} from "../../../model/job/BillOfMaterialsPreview";

/**
 * Bill of Materials fetched successful action.
 * @param {BillOfMaterialsPreviewDetails} bom fetched bill of materials.
 * @param {string} jobId job id of bill of materials.
 * @return {Action<BILL_OF_MATERIALS_FETCHED>}
 */
export function billOfMaterialsFetched(bom: Bom, jobId: string): Action<BILL_OF_MATERIALS_FETCHED> {
	return {type: BILL_OF_MATERIALS_FETCHED, payload: {bom, jobId}}
}

/**
 * Job Bill of Materials removed successful action
 * @param {string} jobId job id of Bill of Materials.
 * @returns {Action<BILL_OF_MATERIAL_REMOVED>}
 */
export function removeBillOfMaterial(jobId: string): Action<BILL_OF_MATERIAL_REMOVED> {
	return {type: BILL_OF_MATERIAL_REMOVED, payload: {jobId}};
}

/**
 * Bills of Materials fetched successful action.
 * @param {BillOfMaterialsPreviewDetails} boms fetched bills of materials.
 * @param {string} jobId job id of bill of materials.
 * @return {Action<BILL_OF_MATERIALS_FETCHED>}
 */
export function billsOfMaterialsFetched(boms: BillOfMaterialsPreview[],
	jobId: string): Action<BILLS_OF_MATERIALS_FETCHED> {
	return {type: BILLS_OF_MATERIALS_FETCHED, payload: {boms, jobId}}
}


/**
 * Fetch Bill Of Materials details
 * @param jobId
 * @param {OnSuccessData<BillOfMaterialsPreviewDetails>} onSuccess
 * @param {OnFailure} onFailure
 */
export function fetchBillOfMaterialsByJobId(jobId: string,
	onSuccess: OnSuccessData<Bom>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobBillOfMaterialsApi.fetchJobBillOfMaterialsDetails(jobId).then(bom => {
			dispatch(billOfMaterialsFetched(bom, jobId));
			onSuccess(bom)
		}).catch(onFailure);
	}
}

/**
 * Cancel selection of bill of materials for the job
 * @param bomId
 * @param {string} jobId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 */
export function cancelBillOfMaterials(bomId: string,
	jobId: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobBillOfMaterialsApi.cancelBOMSelection(bomId)
			.then(() => {
				dispatch(removeBillOfMaterial(jobId));
				onSuccess();
			}).catch(onFailure);
	}
}

/**
 * Add asset to bill of materials
 * @param {string} bomId
 * @param {string} assetId
 * @param {string} jobId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 */
export function addAssetToBillOfMaterials(bomId: string,
	assetId: string,
	jobId: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobBillOfMaterialsApi.addAssetToBillOfMaterials(bomId, assetId)
			.then(bom => {
				dispatch(billOfMaterialsFetched(bom, jobId));
				onSuccess()
			})
			.catch(onFailure);
	}
}

/**
 * Remove asset from bill of materials
 * @param {string} bomId
 * @param {string} assetId
 * @param {string} jobId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 */
export function removeAssetFromBillOfMaterials(bomId: string,
	assetId: string,
	jobId: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobBillOfMaterialsApi.removeAssetFromBillOfMaterials(bomId, assetId)
			.then(bom => {
				dispatch(billOfMaterialsFetched(bom, jobId));
				onSuccess()
			})
			.catch(onFailure);
	}
}

/**
 * Update bill of materials asset
 * @param {string} bomId
 * @param {string} assetId
 * @param {CreateUpdateBillOfMaterialsPreviewAssetApiDto} bomAsset
 * @param {string} jobId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 */
export function updateBillOfMaterialsAsset(bomId: string,
	assetId: string,
	bomAsset: CreateUpdateBillOfMaterialsPreviewAssetApiDto,
	jobId: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobBillOfMaterialsApi.updateBillOfMaterialsAsset(bomId, assetId, bomAsset)
			.then(bom => {
				dispatch(billOfMaterialsFetched(bom, jobId));
				onSuccess()
			})
			.catch(onFailure);
	}
}

/**
 * Add material to asset on bill of materials
 * @param {string} bomId
 * @param {string} assetId
 * @param {string} materialId
 * @param {string} jobId
 * @param {OnSuccessData<Price>} onSuccess
 * @param {OnFailure} onFailure
 */
export function addMaterialToAssetOnBillOfMaterials(bomId: string, assetId: string, materialId: string, jobId: string,
	onSuccess: OnSuccessData<SuggestedAssetPrice>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobBillOfMaterialsApi.addMaterialToAssetOnBillOfMaterials(bomId, assetId, materialId)
			.then(resp => {
				dispatch(billOfMaterialsFetched(resp.bom, jobId));
				onSuccess(resp.suggestedAssetPrice)
			})
			.catch(onFailure);
	}
}

/**
 * Remove material from bill of materials
 * @param {string} bomId
 * @param {string} assetId
 * @param {string} materialId
 * @param {string} jobId
 * @param {OnSuccessData<Price>} onSuccess
 * @param {OnFailure} onFailure
 */
export function removeMaterialFromAssetOnBillOfMaterials(bomId: string, assetId: string, materialId: string, jobId: string,
	onSuccess: OnSuccessData<SuggestedAssetPrice>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobBillOfMaterialsApi.removeMaterialFromAssetOnBillOfMaterials(bomId, assetId, materialId)
			.then(resp => {
				dispatch(billOfMaterialsFetched(resp.bom, jobId));
				onSuccess(resp.suggestedAssetPrice)
			})
			.catch(onFailure);
	}
}

/**
 * Update bill of materials asset sell price
 * @param {string} bomId
 * @param {string} assetId
 * @param {PriceApiDto} suggestedAssetSellPrice
 * @param {string} jobId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 */
export function updateBillOfMaterialsAssetSellPrice(bomId: string,
	assetId: string,
	suggestedAssetSellPrice: PriceApiDto,
	jobId: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobBillOfMaterialsApi.updateBillOfMaterialsAssetSellPrice(bomId, assetId, suggestedAssetSellPrice)
			.then(bom => {
				dispatch(billOfMaterialsFetched(bom, jobId));
				onSuccess()
			})
			.catch(onFailure);
	}
}

/**
 * Finalise Bill of Materials
 * @param {string} bomId
 * @param {{adjust: BillOfMaterialsFinaliseOptions}} selectedOptionData
 * @param {string} jobId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 */
export function finaliseBillOfMaterials(bomId: string,
	selectedOptionData: Option<BillOfMaterialsFinaliseOptions>,
	jobId: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobBillOfMaterialsApi.finaliseBillOfMaterials(bomId, selectedOptionData)
			.then(bom => {
				dispatch(billOfMaterialsFetched(bom, jobId));
				onSuccess()
			})
			.catch(onFailure);
	}
}

export function setMerchantBranchLocation(bomId: string, merchantBranchLocationId: string, jobId: string, onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobBillOfMaterialsApi.updateBillOfMaterials(bomId, merchantBranchLocationId)
			.then(bom => {
				dispatch(billOfMaterialsFetched(bom, jobId));
				onSuccess()
			})
			.catch(onFailure);
	}
}

/**
 * Create bill of material
 * @param jobId
 * @param merchantId
 * @param onSuccess
 * @param onFailure
 */
export function createBillOfMaterial(jobId: string,
	merchantId: string,
	onSuccess: OnSuccessData<Bom>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobBillOfMaterialsApi.createBillOfMaterials(jobId, merchantId)
			.then(bomDetails => {
				dispatch(billOfMaterialsFetched(bomDetails, jobId))
				onSuccess(bomDetails)
			})
			.catch(onFailure)
	}
}

/**
 * Fetch Bills Of Materials details
 * @param jobId
 * @param {OnSuccessData<BillOfMaterialsPreviewDetails[]>} onSuccess
 * @param {OnFailure} onFailure
 */
export function fetchBillsOfMaterialsByJobId(jobId: string,
	onSuccess: OnSuccessData<BillOfMaterialsPreview[]>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.jobBillOfMaterialsApi.fetchJobBillOfMaterialsPreviews(jobId).then(boms => {
			dispatch(billsOfMaterialsFetched(boms, jobId));
			onSuccess(boms)
		}).catch(onFailure);
	}
}